import { ChangeEvent, FC } from 'react';

import {
    ChevronDownIcon,
    Controller,
    RadioButtonGroup,
    StirlingIcon,
    TextField,
} from '@selina-finance/ui';
import { Formik } from 'formik';
import { pick } from 'lodash';

import {
    getDefaultValues,
    getFormHelpers,
    getValidationSchema,
    radioButtonOptions,
} from '@Shared/helpers/input';
import { contractor } from '@src/app/formModels/eligibility/incomings.contractor';

import ErrorContainer from '../../components/ErrorContainer/ErrorContainer';
import SelectBox from '../../components/SelectBox/SelectBox';

import ExpectedDecrease from './IncomingExpectedDecreaseField';
import IncomingOtherIncomesSubView from './IncomingOtherIncomesSubView';

export interface IncomingSelfEmployedViewProps {
    className?: string;
    secondary?: boolean;
    incomeData?: any;
    setRef?: Function;
}

const defaultValues = getDefaultValues(contractor);
const validationSchema = getValidationSchema(contractor);

const IncomingContractorView: FC<IncomingSelfEmployedViewProps> = (props) => {
    return (
        <Formik
            initialValues={(() => {
                const base = {
                    ...defaultValues,
                    ...pick(props.incomeData, Object.keys(contractor)),
                };
                if (base.contractStartDate && !base.startMonth) {
                    const parts = base.contractStartDate.split('/');
                    base.startMonth = parts[1] || '';
                    base.startYear = parts[2] || '';
                }
                if (base.contractEndDate && !base.endMonth) {
                    const parts = base.contractEndDate.split('/');
                    base.endMonth = parts[1] || '';
                    base.endYear = parts[2] || '';
                }
                return base;
            })()}
            validationSchema={validationSchema}
            onSubmit={(values) => values}
            innerRef={(ref) => {
                if (props.setRef) props.setRef(ref);
            }}
        >
            {(formikProps) => {
                const { getError, getPropsWithError } = getFormHelpers(
                    contractor,
                    formikProps,
                );

                return (
                    <div className='grid grid-cols-1 gap-10 m-0'>
                        <Controller label='Are you a first time contractor?'>
                            <RadioButtonGroup
                                name='firstTimeContractor'
                                id='firstTimeContractor'
                                value={
                                    formikProps.values.firstTimeContractor?.toString() ||
                                    ''
                                }
                                error={{
                                    hasError: false,
                                    errorMessage: getError(
                                        'firstTimeContractor',
                                    ),
                                }}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>,
                                ) => {
                                    formikProps.setTouched({
                                        ...formikProps.touched,
                                        firstTimeContractor: true,
                                    });
                                    formikProps.setFieldValue(
                                        'firstTimeContractor',
                                        e.currentTarget.value === 'true',
                                    );
                                }}
                                options={radioButtonOptions}
                            />
                        </Controller>
                        <Controller label='When did your contract start?'>
                            <div className='grid grid-cols-1 gap-2 grid-cols-2'>
                                <SelectBox
                                    items={contractor.month.items}
                                    suffix={<ChevronDownIcon />}
                                    label='Month'
                                    labelSize='small'
                                    placeholder='Select'
                                    name='startMonth'
                                    validState={
                                        !!formikProps.values?.startMonth
                                    }
                                    value={
                                        formikProps.values?.startMonth?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const month = e.currentTarget.value;
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            startMonth: true,
                                        });
                                        formikProps.setFieldValue(
                                            'startMonth',
                                            month,
                                        );
                                        if (formikProps.values.startYear) {
                                            formikProps.setFieldValue(
                                                'contractStartDate',
                                                `01/${month}/${formikProps.values.startYear}`,
                                            );
                                        }
                                    }}
                                    error={getError('startMonth')}
                                    onBlur={() => {
                                        return formikProps.setTouched({
                                            ...formikProps.touched,
                                            startMonth: true,
                                        });
                                    }}
                                />
                                <SelectBox
                                    items={contractor.year.items}
                                    suffix={<ChevronDownIcon />}
                                    label='Year'
                                    labelSize='small'
                                    placeholder='Select'
                                    name='startYear'
                                    validState={!!formikProps.values?.startYear}
                                    value={
                                        formikProps.values?.startYear?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const year = e.currentTarget.value;
                                        formikProps.setFieldValue(
                                            'startYear',
                                            year,
                                        );
                                        if (formikProps.values.startMonth) {
                                            formikProps.setFieldValue(
                                                'contractStartDate',
                                                `01/${formikProps.values.startMonth}/${year}`,
                                            );
                                        }
                                    }}
                                    error={getError('startYear')}
                                    onBlur={() => {
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            startYear: true,
                                            contractStartDate: true,
                                        });
                                    }}
                                />
                            </div>
                            {formikProps.values.startMonth &&
                                formikProps.values.startYear &&
                                formikProps.errors.contractStartDate && (
                                    <div className='text-error-100 text-md -mt-5 inline'>
                                        {getError('contractStartDate')}
                                    </div>
                                )}
                        </Controller>
                        <Controller label='When does your contract end?'>
                            <div className='grid grid-cols-1 gap-2 grid-cols-2'>
                                <SelectBox
                                    items={contractor.month.items}
                                    suffix={<ChevronDownIcon />}
                                    label='Month'
                                    labelSize='small'
                                    placeholder='Select'
                                    name='endMonth'
                                    validState={!!formikProps.values?.endMonth}
                                    value={
                                        formikProps.values?.endMonth?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const month = e.currentTarget.value;
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            endMonth: true,
                                        });
                                        formikProps.setFieldValue(
                                            'endMonth',
                                            month,
                                        );
                                        if (formikProps.values.endYear) {
                                            formikProps.setFieldValue(
                                                'contractEndDate',
                                                `01/${month}/${formikProps.values.endYear}`,
                                            );
                                        }
                                    }}
                                    error={getError('endMonth')}
                                    onBlur={() => {
                                        return formikProps.setTouched({
                                            ...formikProps.touched,
                                            endMonth: true,
                                        });
                                    }}
                                />
                                <SelectBox
                                    items={contractor.futureYear.items}
                                    suffix={<ChevronDownIcon />}
                                    label='Year'
                                    labelSize='small'
                                    placeholder='Select'
                                    name='endYear'
                                    validState={!!formikProps.values?.endYear}
                                    value={
                                        formikProps.values?.endYear?.toString() ||
                                        ''
                                    }
                                    onChange={(
                                        e: ChangeEvent<HTMLInputElement>,
                                    ) => {
                                        const year = e.currentTarget.value;
                                        formikProps.setFieldValue(
                                            'endYear',
                                            year,
                                        );
                                        if (formikProps.values.endMonth) {
                                            formikProps.setFieldValue(
                                                'contractEndDate',
                                                `01/${formikProps.values.endMonth}/${year}`,
                                            );
                                        }
                                    }}
                                    error={getError('endYear')}
                                    onBlur={() => {
                                        formikProps.setTouched({
                                            ...formikProps.touched,
                                            endYear: true,
                                            contractEndDate: true,
                                        });
                                    }}
                                />
                            </div>
                            {formikProps.values.endMonth &&
                                formikProps.values.endYear &&
                                formikProps.errors.contractEndDate && (
                                    <div className='text-error-100 text-md -mt-5 inline'>
                                        {getError('contractEndDate')}
                                    </div>
                                )}
                        </Controller>
                        <TextField
                            label='What is your contract day rate?'
                            name='contractDayRateReported'
                            mask={'currency' as any}
                            prefix={<StirlingIcon />}
                            maxLength={10}
                            value={
                                formikProps.values.contractDayRateReported?.toString() ||
                                ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('contractDayRateReported')}
                        />
                        <TextField
                            label='How many days per week do you work?'
                            name='contractDaysWorkedWeeklyReported'
                            mask='number'
                            maxLength={1}
                            value={
                                formikProps.values?.contractDaysWorkedWeeklyReported?.toString() ||
                                ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('contractDaysWorkedWeeklyReported')}
                        />
                        <TextField
                            label='What is your job title?'
                            name='jobTitle'
                            maxLength={255}
                            value={
                                formikProps.values?.jobTitle?.toString() || ''
                            }
                            onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                formikProps.handleChange(e)
                            }
                            onBlur={formikProps.handleBlur}
                            error={getError('jobTitle')}
                        />
                        <Controller
                            label='Do you earn any other income?'
                            additionalInfo='Such as bonus, dividends, pension, second job.'
                        >
                            <IncomingOtherIncomesSubView
                                title='Other incomes:'
                                addButtonTitle='Add an income source'
                                formikProps={formikProps}
                                getPropsWithError={getPropsWithError}
                                getError={getError}
                            />
                        </Controller>
                        <Controller
                            label="At what age do you think you'll retire?"
                            additionalInfo='For most people this will be 66'
                        >
                            <TextField
                                name='estimatedRetirementAge'
                                mask='number'
                                maxLength={2}
                                value={
                                    formikProps.values?.estimatedRetirementAge?.toString() ||
                                    ''
                                }
                                onChange={(e: ChangeEvent<HTMLInputElement>) =>
                                    formikProps.handleChange(e)
                                }
                                onBlur={formikProps.handleBlur}
                                error={getError('estimatedRetirementAge')}
                            />
                        </Controller>
                        <Controller label='Do you expect your income to decrease in the near future?'>
                            <ExpectedDecrease
                                formikProps={formikProps}
                                getPropsWithError={getPropsWithError}
                                getError={getError}
                            />
                        </Controller>
                        <ErrorContainer formikProps={formikProps} />
                    </div>
                );
            }}
        </Formik>
    );
};

export default IncomingContractorView;
